/** Ensures project has a map image (project_instance_map_img_url). Called after publish/unpublish/archive. */

import { convertToSceneOrigin, convertSceneOriginToStory } from '@/utils/dataConverters';
import {
  LUREO_PIN_ANCHOR_X,
  LUREO_PIN_ANCHOR_Y,
  LUREO_PIN_DEFAULT_WIDTH,
  LUREO_PIN_DEFAULT_HEIGHT,
  SVG_VIEWBOX_WIDTH,
  SVG_VIEWBOX_HEIGHT,
  SVG_PIN_CENTER_X,
  SVG_TEXT_Y_BASE,
  SVG_TEXT_OFFSET,
  SVG_PILL_HEIGHT,
  SVG_PILL_RADIUS
} from '@/utils/mapMarkerSvg';
import type { MapSnapshotService } from '@/modules/mapSnapshotService/types';
import { v1 } from '@/infrastructure/api/v1/client';
import { listAll, listAllIn } from '@/infrastructure/api/v1/lists';
import { storyToLegacy } from '@/infrastructure/api/v1/mappers/stories';

const MAP_SNAPSHOT_SIZE = 640;
const SNAPSHOT_MARKER_SCALE = 1.5;
const PROJECT_VIEW_ZOOM = 16;
const STATIC_MAP_SCALE = 2;

export interface EnsureProjectMapImageServices {
  mapSnapshot: MapSnapshotService;
}

export interface EnsureProjectMapImageResult {
  success: boolean;
  skipped?: boolean;
  error?: string;
}

export async function ensureProjectMapImage(
  services: EnsureProjectMapImageServices,
  projectId: string,
  userId: string
): Promise<EnsureProjectMapImageResult> {
  try {
    // listStories is ordered by order number by default
    const sceneOriginsResponse = await listAll(page =>
      v1.GET('/api/v1/stories', { params: { query: { projectUniqueId: [projectId], userUniqueId: userId, ...page } } }),
    );

    if (sceneOriginsResponse.error) {
      console.error('ensureProjectMapImage: fetch scene_origins failed', sceneOriginsResponse.error);
      return { success: false, error: String(sceneOriginsResponse.error.message) };
    }

    const sceneOrigins: any = sceneOriginsResponse.data.map(storyToLegacy);
    if (sceneOrigins.length === 0) return { success: true, skipped: true };

    const sceneOriginIds = sceneOrigins.map((so: { scene_origin_id: string }) => so.scene_origin_id);
    const { data: publishedInstances } = await listAllIn(sceneOriginIds, (storyId, page) =>
      v1.GET('/api/v1/scenes', { params: { query: { storyUniqueId: storyId, status: ['live', 'pending'], ...page } } }),
    );

    const publishedOriginIds = new Set((publishedInstances || []).map(i => i.storyUniqueId));
    const filteredSceneOrigins = sceneOrigins.filter((so: { scene_origin_id: string }) =>
      publishedOriginIds.has(so.scene_origin_id)
    );
    const publishedStories = filteredSceneOrigins.map((sceneOrigin: any, index: number) =>
      convertSceneOriginToStory(convertToSceneOrigin(sceneOrigin), index + 1)
    );

    const storiesWithLocation = publishedStories.filter(
      (s: { locationInfo?: { coordinates?: { lat?: number; lng?: number } } }) =>
        s.locationInfo?.coordinates?.lat != null && s.locationInfo?.coordinates?.lng != null
    );

    if (storiesWithLocation.length === 0) return { success: true, skipped: true };

    const lats = storiesWithLocation.map(
      (s: { locationInfo: { coordinates: { lat: number } } }) => s.locationInfo.coordinates.lat
    );
    const lngs = storiesWithLocation.map(
      (s: { locationInfo: { coordinates: { lng: number } } }) => s.locationInfo.coordinates.lng
    );

    const centerLat = lats.reduce((sum, lat) => sum + lat, 0) / lats.length;
    const centerLng = lngs.reduce((sum, lng) => sum + lng, 0) / lngs.length;

    const padding = 0.003;
    const bounds = {
      north: Math.max(...lats) + padding,
      south: Math.min(...lats) - padding,
      east: Math.max(...lngs) + padding,
      west: Math.min(...lngs) - padding
    };

    let zoomLevel = PROJECT_VIEW_ZOOM;
    if (storiesWithLocation.length > 1) {
      const worldDim = { height: 256, width: 256 };
      const mapDim = {
        height: MAP_SNAPSHOT_SIZE * STATIC_MAP_SCALE,
        width: MAP_SNAPSHOT_SIZE * STATIC_MAP_SCALE
      };

      const latRad = (lat: number) => {
        const sin = Math.sin((lat * Math.PI) / 180);
        return Math.log((1 + sin) / (1 - sin)) / 2;
      };

      const latFraction = (latRad(bounds.north) - latRad(bounds.south)) / Math.PI;
      const lngDiff = bounds.east - bounds.west;
      const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

      const zoomLat =
        latFraction > 0 ? Math.log2(mapDim.height / worldDim.height / latFraction) : PROJECT_VIEW_ZOOM;
      const zoomLng =
        lngFraction > 0 ? Math.log2(mapDim.width / worldDim.width / lngFraction) : PROJECT_VIEW_ZOOM;

      const computedZoom = Math.min(zoomLat, zoomLng, 20);
      zoomLevel = Math.max(0, Math.floor(computedZoom));
    }

    const mapSettings = {
      center: { lat: centerLat, lng: centerLng },
      zoom: zoomLevel,
      bounds,
      markerAnchor: {
        x: LUREO_PIN_ANCHOR_X * SNAPSHOT_MARKER_SCALE,
        y: LUREO_PIN_ANCHOR_Y * SNAPSHOT_MARKER_SCALE
      },
      markerSize: {
        width: LUREO_PIN_DEFAULT_WIDTH * SNAPSHOT_MARKER_SCALE,
        height: LUREO_PIN_DEFAULT_HEIGHT * SNAPSHOT_MARKER_SCALE
      },
      mapSize: { width: MAP_SNAPSHOT_SIZE, height: MAP_SNAPSHOT_SIZE },
      svgViewBox: { width: SVG_VIEWBOX_WIDTH, height: SVG_VIEWBOX_HEIGHT },
      svgPinCenter: { x: SVG_PIN_CENTER_X },
      svgTextSettings: {
        yBase: SVG_TEXT_Y_BASE,
        offset: SVG_TEXT_OFFSET,
        pillHeight: SVG_PILL_HEIGHT,
        pillRadius: SVG_PILL_RADIUS
      }
    };

    const serviceStories = storiesWithLocation.map(
      (s: { id: string; title: string; order?: number; locationInfo: { coordinates: { lat: number; lng: number } } }) => ({
        id: s.id,
        title: s.title,
        order: s.order,
        latitude: s.locationInfo.coordinates.lat,
        longitude: s.locationInfo.coordinates.lng
      })
    );

    const imageUrl = await services.mapSnapshot.saveProjectMapSnapshot({
      projectInstanceId: projectId,
      stories: serviceStories,
      userId,
      mapSettings
    });

    if (imageUrl == null) {
      console.error('ensureProjectMapImage: map snapshot returned null', projectId);
      return { success: false, error: 'Map snapshot service returned null' };
    }
    return { success: true };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('ensureProjectMapImage failed', error);
    return { success: false, error: message };
  }
}
